import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Connection Status
  connectionStatus: {
    status: 'ONLINE',
    lastChecked: new Date().toISOString(),
    uptime: 98.7,
    downtimeMinutes: 22,
    avgResponseTime: 1.2,
    recentRequests: 1000
  },

  // SSO Configuration
  ssoConfig: {
    endpointUrl: '',
    systemId: '',
    authKey: '',
    connectionTimeout: 41,
    requestTimeout: 30
  },

  // Recent Events
  recentEvents: [],

  // VNeID Accounts Management (Tab 2)
  accounts: {
    list: [],
    selectedAccount: null,
    selectedIds: [],
    total: 0,
    page: 1,
    pageSize: 10,
    sortColumn: 'createdOn',
    sortDirection: 'desc',
    filters: {
      unitName: '',
      accountName: '',
      status: null
    },
    statistics: {
      activeAccounts: 0,
      activeAccountsChangePercent: 0,
      lockedAccounts: 0,
      queriesToday: 0,
      queriesTodayPercent: 0,
      totalQueriesLimit: 0,
      usageThisMonth: 0,
      usagePreviousMonth: 0,
      usageChangePercent: 0
    }
  },

  // Tab State
  activeTab: 0,

  // Loading states
  loading: {
    ping: false,
    refreshToken: false,
    monitoring: false,
    checkInterruptions: false,
    fetchConfig: false,
    saveConfig: false,
    fetchAccounts: false,
    fetchAccountById: false,
    fetchStatistics: false,
    createAccount: false,
    updateAccount: false,
    deleteAccount: false,
    bulkDeleteAccounts: false,
    toggleAccountStatus: false,
    regenerateApiKey: false,
    revokeApiKey: false,
    assignSdk: false
  },

  // Error states
  errors: {
    ping: null,
    refreshToken: null,
    monitoring: null,
    checkInterruptions: null,
    fetchConfig: null,
    saveConfig: null,
    accounts: null
  },

  // Success messages
  success: {
    ping: null,
    refreshToken: null,
    saveConfig: null,
    accounts: null
  }
};

const vneidStatisticsSlice = createSlice({
  name: 'vneidStatistics',
  initialState,
  reducers: {
    // Tab management
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },

    // Fetch SSO Configuration
    fetchSSOConfig: (state) => {
      state.loading.fetchConfig = true;
      state.errors.fetchConfig = null;
    },
    fetchSSOConfigSuccess: (state, action) => {
      state.loading.fetchConfig = false;
      state.ssoConfig = action.payload;
    },
    fetchSSOConfigFailure: (state, action) => {
      state.loading.fetchConfig = false;
      state.errors.fetchConfig = action.payload;
    },

    // Update SSO Configuration
    updateSSOConfig: (state, action) => {
      state.ssoConfig = { ...state.ssoConfig, ...action.payload };
    },

    // Save SSO Configuration
    saveSSOConfig: (state) => {
      state.loading.saveConfig = true;
      state.errors.saveConfig = null;
      state.success.saveConfig = null;
    },
    saveSSOConfigSuccess: (state, action) => {
      state.loading.saveConfig = false;
      state.success.saveConfig = action.payload;
    },
    saveSSOConfigFailure: (state, action) => {
      state.loading.saveConfig = false;
      state.errors.saveConfig = action.payload;
    },

    // Ping Connection
    pingConnection: (state) => {
      state.loading.ping = true;
      state.errors.ping = null;
      state.success.ping = null;
    },
    pingConnectionSuccess: (state, action) => {
      state.loading.ping = false;
      state.success.ping = action.payload.message;
      state.connectionStatus = {
        ...state.connectionStatus,
        lastChecked: new Date().toISOString(),
        avgResponseTime: action.payload.responseTime || state.connectionStatus.avgResponseTime
      };
    },
    pingConnectionFailure: (state, action) => {
      state.loading.ping = false;
      state.errors.ping = action.payload;
      state.connectionStatus.status = 'OFFLINE';
    },

    // Refresh Token
    refreshToken: (state) => {
      state.loading.refreshToken = true;
      state.errors.refreshToken = null;
      state.success.refreshToken = null;
    },
    refreshTokenSuccess: (state, action) => {
      state.loading.refreshToken = false;
      state.success.refreshToken = action.payload;
    },
    refreshTokenFailure: (state, action) => {
      state.loading.refreshToken = false;
      state.errors.refreshToken = action.payload;
    },

    // Monitor Connection
    startMonitoring: (state) => {
      state.loading.monitoring = true;
      state.errors.monitoring = null;
    },
    stopMonitoring: (state) => {
      state.loading.monitoring = false;
    },
    updateConnectionStatus: (state, action) => {
      state.connectionStatus = { ...state.connectionStatus, ...action.payload };
    },

    // Check Interruptions
    checkInterruptions: (state) => {
      state.loading.checkInterruptions = true;
      state.errors.checkInterruptions = null;
    },
    checkInterruptionsSuccess: (state, action) => {
      state.loading.checkInterruptions = false;
      state.recentEvents = action.payload;
    },
    checkInterruptionsFailure: (state, action) => {
      state.loading.checkInterruptions = false;
      state.errors.checkInterruptions = action.payload;
    },

    // Fetch Recent Events
    fetchRecentEvents: (state) => {
      state.loading.fetchConfig = true;
    },
    fetchRecentEventsSuccess: (state, action) => {
      state.loading.fetchConfig = false;
      state.recentEvents = action.payload;
    },
    fetchRecentEventsFailure: (state, action) => {
      state.loading.fetchConfig = false;
      state.errors.fetchConfig = action.payload;
    },

    // ========== VNeID Accounts Management (Tab 2) ==========

    // Fetch Accounts
    fetchAccounts: (state) => {
      state.loading.fetchAccounts = true;
      state.errors.accounts = null;
    },
    fetchAccountsSuccess: (state, action) => {
      state.loading.fetchAccounts = false;
      state.accounts.list = action.payload.data;
      state.accounts.total = action.payload.meta.total;
    },
    fetchAccountsFailure: (state, action) => {
      state.loading.fetchAccounts = false;
      state.errors.accounts = action.payload;
    },

    // Fetch Account by ID
    fetchAccountById: (state) => {
      state.loading.fetchAccountById = true;
      state.errors.accounts = null;
    },
    fetchAccountByIdSuccess: (state, action) => {
      state.loading.fetchAccountById = false;
      state.accounts.selectedAccount = action.payload;
    },
    fetchAccountByIdFailure: (state, action) => {
      state.loading.fetchAccountById = false;
      state.errors.accounts = action.payload;
    },

    // Fetch Statistics
    fetchStatistics: (state) => {
      state.loading.fetchStatistics = true;
      state.errors.accounts = null;
    },
    fetchStatisticsSuccess: (state, action) => {
      state.loading.fetchStatistics = false;
      state.accounts.statistics = action.payload;
    },
    fetchStatisticsFailure: (state, action) => {
      state.loading.fetchStatistics = false;
      state.errors.accounts = action.payload;
    },

    // Create Account
    createAccount: (state) => {
      state.loading.createAccount = true;
      state.errors.accounts = null;
      state.success.accounts = null;
    },
    createAccountSuccess: (state, action) => {
      state.loading.createAccount = false;
      state.success.accounts = action.payload.message;
    },
    createAccountFailure: (state, action) => {
      state.loading.createAccount = false;
      state.errors.accounts = action.payload;
    },

    // Update Account
    updateAccount: (state) => {
      state.loading.updateAccount = true;
      state.errors.accounts = null;
      state.success.accounts = null;
    },
    updateAccountSuccess: (state, action) => {
      state.loading.updateAccount = false;
      state.success.accounts = action.payload.message;
    },
    updateAccountFailure: (state, action) => {
      state.loading.updateAccount = false;
      state.errors.accounts = action.payload;
    },

    // Delete Account
    deleteAccount: (state) => {
      state.loading.deleteAccount = true;
      state.errors.accounts = null;
      state.success.accounts = null;
    },
    deleteAccountSuccess: (state, action) => {
      state.loading.deleteAccount = false;
      state.success.accounts = action.payload.message;
    },
    deleteAccountFailure: (state, action) => {
      state.loading.deleteAccount = false;
      state.errors.accounts = action.payload;
    },

    // Bulk Delete Accounts
    bulkDeleteAccounts: (state) => {
      state.loading.bulkDeleteAccounts = true;
      state.errors.accounts = null;
      state.success.accounts = null;
    },
    bulkDeleteAccountsSuccess: (state, action) => {
      state.loading.bulkDeleteAccounts = false;
      state.success.accounts = action.payload.message;
      state.accounts.selectedIds = [];
    },
    bulkDeleteAccountsFailure: (state, action) => {
      state.loading.bulkDeleteAccounts = false;
      state.errors.accounts = action.payload;
    },

    // Toggle Account Status
    toggleAccountStatus: (state) => {
      state.loading.toggleAccountStatus = true;
      state.errors.accounts = null;
      state.success.accounts = null;
    },
    toggleAccountStatusSuccess: (state, action) => {
      state.loading.toggleAccountStatus = false;
      state.success.accounts = action.payload.message;
    },
    toggleAccountStatusFailure: (state, action) => {
      state.loading.toggleAccountStatus = false;
      state.errors.accounts = action.payload;
    },

    // Regenerate API Key
    regenerateApiKey: (state) => {
      state.loading.regenerateApiKey = true;
      state.errors.accounts = null;
      state.success.accounts = null;
    },
    regenerateApiKeySuccess: (state, action) => {
      state.loading.regenerateApiKey = false;
      state.success.accounts = action.payload.message;
    },
    regenerateApiKeyFailure: (state, action) => {
      state.loading.regenerateApiKey = false;
      state.errors.accounts = action.payload;
    },

    // Revoke API Key
    revokeApiKey: (state) => {
      state.loading.revokeApiKey = true;
      state.errors.accounts = null;
      state.success.accounts = null;
    },
    revokeApiKeySuccess: (state, action) => {
      state.loading.revokeApiKey = false;
      state.success.accounts = action.payload.message;
    },
    revokeApiKeyFailure: (state, action) => {
      state.loading.revokeApiKey = false;
      state.errors.accounts = action.payload;
    },

    // Assign SDK to Account
    assignSdk: (state) => {
      state.loading.assignSdk = true;
      state.errors.accounts = null;
      state.success.accounts = null;
    },
    assignSdkSuccess: (state, action) => {
      state.loading.assignSdk = false;
      state.success.accounts = action.payload.message;
    },
    assignSdkFailure: (state, action) => {
      state.loading.assignSdk = false;
      state.errors.accounts = action.payload;
    },

    // Pagination
    setAccountsPage: (state, action) => {
      state.accounts.page = action.payload;
    },
    setAccountsPageSize: (state, action) => {
      state.accounts.pageSize = action.payload;
      state.accounts.page = 1;
    },

    // Sorting
    setAccountsSortColumn: (state, action) => {
      state.accounts.sortColumn = action.payload;
    },
    setAccountsSortDirection: (state, action) => {
      state.accounts.sortDirection = action.payload;
    },

    // Filtering
    setAccountsFilterUnitName: (state, action) => {
      state.accounts.filters.unitName = action.payload;
      state.accounts.page = 1;
    },
    setAccountsFilterAccountName: (state, action) => {
      state.accounts.filters.accountName = action.payload;
      state.accounts.page = 1;
    },
    setAccountsFilterStatus: (state, action) => {
      state.accounts.filters.status = action.payload;
      state.accounts.page = 1;
    },

    // Selection
    setAccountsSelectedIds: (state, action) => {
      state.accounts.selectedIds = action.payload;
    },

    // Clear selected account
    clearSelectedAccount: (state) => {
      state.accounts.selectedAccount = null;
    },

    // Clear messages
    clearMessages: (state) => {
      state.errors = {
        ping: null,
        refreshToken: null,
        monitoring: null,
        checkInterruptions: null,
        fetchConfig: null,
        saveConfig: null,
        accounts: null
      };
      state.success = {
        ping: null,
        refreshToken: null,
        saveConfig: null,
        accounts: null
      };
    }
  }
});

export const {
  setActiveTab,
  fetchSSOConfig,
  fetchSSOConfigSuccess,
  fetchSSOConfigFailure,
  updateSSOConfig,
  saveSSOConfig,
  saveSSOConfigSuccess,
  saveSSOConfigFailure,
  pingConnection,
  pingConnectionSuccess,
  pingConnectionFailure,
  refreshToken,
  refreshTokenSuccess,
  refreshTokenFailure,
  startMonitoring,
  stopMonitoring,
  updateConnectionStatus,
  checkInterruptions,
  checkInterruptionsSuccess,
  checkInterruptionsFailure,
  fetchRecentEvents,
  fetchRecentEventsSuccess,
  fetchRecentEventsFailure,
  fetchAccounts,
  fetchAccountsSuccess,
  fetchAccountsFailure,
  fetchAccountById,
  fetchAccountByIdSuccess,
  fetchAccountByIdFailure,
  fetchStatistics,
  fetchStatisticsSuccess,
  fetchStatisticsFailure,
  createAccount,
  createAccountSuccess,
  createAccountFailure,
  updateAccount,
  updateAccountSuccess,
  updateAccountFailure,
  deleteAccount,
  deleteAccountSuccess,
  deleteAccountFailure,
  bulkDeleteAccounts,
  bulkDeleteAccountsSuccess,
  bulkDeleteAccountsFailure,
  toggleAccountStatus,
  toggleAccountStatusSuccess,
  toggleAccountStatusFailure,
  regenerateApiKey,
  regenerateApiKeySuccess,
  regenerateApiKeyFailure,
  revokeApiKey,
  revokeApiKeySuccess,
  revokeApiKeyFailure,
  assignSdk,
  assignSdkSuccess,
  assignSdkFailure,
  setAccountsPage,
  setAccountsPageSize,
  setAccountsSortColumn,
  setAccountsSortDirection,
  setAccountsFilterUnitName,
  setAccountsFilterAccountName,
  setAccountsFilterStatus,
  setAccountsSelectedIds,
  clearSelectedAccount,
  clearMessages
} = vneidStatisticsSlice.actions;

export default vneidStatisticsSlice.reducer;
